import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  Skeleton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import ModalMAIQ from 'app/components/ModalMAIQ';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ConfirmationModal from 'app/components/ConfirmationModal';
import { formatDateForDisplay, parseDateValue } from 'utils/dateUtils';
import DealerSpecialForm, { DealerSpecialFormData, DealerSpecialFormErrors } from './DealerSpecialForm';

interface DealerSpecialsDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  onSave: (payload: any) => void;
  onDelete: () => void;
}

const DealerSpecialsDetailsModal: React.FC<DealerSpecialsDetailsModalProps> = ({
  open,
  onClose,
  data,
  onSave,
  onDelete,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState<DealerSpecialFormErrors>({});
  const [apiError, setApiError] = React.useState<string>('');
  const [formData, setFormData] = React.useState<DealerSpecialFormData>({
    special_headline: '',
    special_description: '',
    start_date: '',
    end_date: '',
    applicable_model: [],
    disclaimer_text: '',
  });

  React.useEffect(() => {
    if (data) {
      setFormData({
        special_headline: data.special_headline || '',
        special_description: data.special_description || '',
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        applicable_model: Array.isArray(data.applicable_model) 
          ? data.applicable_model 
          : data.applicable_model 
            ? [data.applicable_model] 
            : [],
        disclaimer_text: data.disclaimer_text || '',
      });
    }
    setFormErrors({});
    setApiError('');
  }, [data]);

  React.useEffect(() => {
    if (!isEditing) {
      setFormErrors({});
      setApiError('');
    }
  }, [isEditing]);

  React.useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setFormErrors({});
      setApiError('');
    }
  }, [open]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear field-specific error when user starts typing
    if (formErrors[field as keyof DealerSpecialFormErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof DealerSpecialFormErrors];
        return newErrors;
      });
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = (): boolean => {
    const errors: DealerSpecialFormErrors = {};

    if (!formData.special_headline?.trim()) {
      errors.special_headline = 'Special headline is required';
    }
    if (!formData.special_description?.trim()) {
      errors.special_description = 'Special description is required';
    }
    if (!formData.start_date?.trim()) {
      errors.start_date = 'Start date is required';
    }
    if (!formData.end_date?.trim()) {
      errors.end_date = 'End date is required';
    }
    if (!formData.applicable_model || formData.applicable_model.length === 0) {
      errors.applicable_model = 'Applicable model is required';
    }

    const startDate = parseDateValue(formData.start_date);
    const endDate = parseDateValue(formData.end_date);

    if (!startDate || !endDate) {
      if (!startDate) errors.start_date = 'Start date is invalid';
      if (!endDate) errors.end_date = 'End date is invalid';
    } else {
      const startMillis = startDate.startOf('day').toMillis();
      const endMillis = endDate.startOf('day').toMillis();

      if (endMillis <= startMillis) {
        errors.end_date = 'End date must be after start date';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;
    setSaving(true);
    setApiError('');
    try {
      await onSave(formData);
      setIsEditing(false);
      setFormErrors({});
      onClose(); // Close modal after successful save
    } catch (error) {
      setApiError(
        error instanceof Error ? error.message : 'Failed to save dealer special',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{ width: '600px', maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <Box
          className="modal-header"
          sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" fontWeight={600}>
            Dealer Special Details
          </Typography>
          <Box>
            <Tooltip title="Edit">
              <IconButton
                className="modal-header-button"
                size="small"
                onClick={() => setIsEditing(true)}
                disabled={isEditing}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton className="modal-header-button" size="small" sx={{ ml: 1 }} onClick={() => setOpenDeleteConfirm(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton className="modal-header-button" size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, overflowY: 'auto' }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          {isEditing ? (
            <DealerSpecialForm
              formData={formData}
              onFormChange={handleFormChange}
              errors={formErrors}
              disabled={saving}
            />
          ) : (
            <>
              {/* Special Headline */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Special Headline
                </Typography>
                <Typography variant="body2">{data.special_headline}</Typography>
              </Box>

              {/* Special Description */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Special Description
                </Typography>
                <Typography variant="body2">{data.special_description}</Typography>
              </Box>

              {/* Start Date */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Start Date
                </Typography>
                <Typography variant="body2">
                  {formatDateForDisplay(data.start_date) || 'No start date'}
                </Typography>
              </Box>

              {/* End Date */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  End Date
                </Typography>
                <Typography variant="body2">
                  {formatDateForDisplay(data.end_date) || 'No end date'}
                </Typography>
              </Box>

              {/* Applicable Model */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Applicable Model
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Array.isArray(data.applicable_model) 
                    ? data.applicable_model.map((value: string) => (
                        <Chip key={value} label={value} size="small" />
                      ))
                    : data.applicable_model ? (
                        <Chip label={data.applicable_model} size="small" />
                      ) : (
                        <Typography variant="body2">No applicable models selected</Typography>
                      )
                  }
                </Box>
              </Box>

              {/* Disclaimer Text */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Disclaimer Text
                </Typography>
                <Typography variant="body2">{data.disclaimer_text || 'No disclaimer'}</Typography>
              </Box>
            </>
          )}
        </Box>
        <Divider />

        {/* Footer action bar */}
        {isEditing && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              zIndex: 9,
              flexShrink: 0,
              gap: 2,
            }}
          >
            <ButtonMAIQ
              variant="outlined"
              onClick={() => {
                setIsEditing(false);
                setFormData({
                  special_headline: data.special_headline || '',
                  special_description: data.special_description || '',
                  start_date: data.start_date || '',
                  end_date: data.end_date || '',
                  applicable_model: Array.isArray(data.applicable_model) 
                    ? data.applicable_model 
                    : data.applicable_model 
                      ? [data.applicable_model] 
                      : [],
                  disclaimer_text: data.disclaimer_text || '',
                });
                setFormErrors({});
                setApiError('');
              }}
            >
              Cancel
            </ButtonMAIQ>
            {saving ? (
              <Skeleton
                variant="rectangular"
                width={140}
                height={36}
                sx={{
                  borderRadius: 1,
                  bgcolor: theme.palette.action.hover,
                }}
              />
            ) : (
              <ButtonMAIQ
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                Save Changes
              </ButtonMAIQ>
            )}
          </Box>
        )}
      </Paper>
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await onDelete();
            setOpenDeleteConfirm(false); // Close confirmation modal after successful deletion
          } catch (error) {
            // Error is handled by parent component, but keep modal open if there's an error
            console.error('Error deleting dealer special:', error);
          } finally {
            setIsDeleting(false);
          }
        }}
        message="Are you sure you want to delete this dealer special?"
        loading={isDeleting}
      />
    </ModalMAIQ>
  );
};

export default DealerSpecialsDetailsModal; 